import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useToast } from '@/components/ui/Toast';
import { enqueueProfileReportSync } from '@/api/accountAdminSync';
import { cn } from '@/utils/cn';

const SYNC_TITLE =
  'Gap-fill last 12 complete calendar months of campaign, keyword, search-term, target, and placement reports. Months already in S3 are skipped; Amazon work is queued one month at a time with 429 backoff.';

export function SyncReportsButton({
  profileId,
  className,
  children = 'Sync reports',
}: {
  profileId: string;
  className?: string;
  children?: ReactNode;
}) {
  const { showToast } = useToast();
  const mutation = useMutation({
    mutationFn: () => enqueueProfileReportSync(profileId),
    onSuccess: () => {
      showToast(
        'Report sync queued. Remaining windows resume automatically if Amazon rate-limits.',
        'success'
      );
    },
    onError: (err) => {
      showToast((err as Error).message || 'Report sync failed to start', 'error');
    },
  });

  return (
    <button
      type="button"
      disabled={!profileId || mutation.isPending}
      title={SYNC_TITLE}
      onClick={() => mutation.mutate()}
      className={cn('disabled:cursor-not-allowed disabled:opacity-60', className)}
    >
      {mutation.isPending ? (
        <span className="inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Syncing…
        </span>
      ) : (
        children
      )}
    </button>
  );
}
